import { useCallback, useEffect, useMemo, useState } from 'react';
import { MapVacancyType, STATUS_ORDER, VACANCY_GROUP_STATUS } from 'domain/shared/newVacancy';
import { useShowContext } from 'framework/show';
import { useRecruitmentApi } from 'infrastructure/api';
import useVacancyPublicationService from '../publications/vacancyPublicationService';
import { Stages } from 'domain/shared/candidate';

type UseVacanciesDataArgs = {
  getVacancyGroupById: (id: string | number) => Promise<any>;
};

export default function useVacanciesData({ getVacancyGroupById }: UseVacanciesDataArgs) {
  const { data, setData = () => {} } = useShowContext();
  const { sendPostRequest } = useRecruitmentApi();
  const { searchWithFilters } = useVacancyPublicationService();
  const [isLoading, setIsLoading] = useState(false);
  const [hasActiveApplications, setHasActiveApplications] = useState(true);
  const [hasActivePublications, setHasActivePublications] = useState(true);
  const [rerenderCount, setRerenderCount] = useState(0);

  const { id, alias, vacancies } = data.getData() || {};

  useEffect(() => {
    (async () => {
      try {
        const applications = await sendPostRequest('/candidates/applications/search', {
          filter: {
            vacancyGroupAlias: { in: [alias] },
            currentPipelineStage: { not_in: [Stages.Rejected, Stages.Closed] },
          },
          pageSize: 1,
        });
        const result = await applications.json();
        setHasActiveApplications(result.totalElements !== 0);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('applications check failed', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const pubs = await searchWithFilters({
          filter: {
            'vacancyGroup.id': { eq: id },
            status: { eq: VACANCY_GROUP_STATUS.OPEN },
          },
          pageSize: 1,
        } as any);

        if (mounted) setHasActivePublications(pubs.totalElements !== 0);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('publications check failed', err);
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshData = useCallback(async () => {
    setIsLoading(true);
    try {
      const newData = await getVacancyGroupById(id);
      setData(newData);
      setRerenderCount(c => c + 1);
    } finally {
      setIsLoading(false);
    }
  }, [id, getVacancyGroupById, setData]);

  const sortedVacancies = useMemo(() => {
    const vacanciesData = (vacancies || []) as MapVacancyType[];
    return [...vacanciesData].sort((a, b) => (STATUS_ORDER[a.status] ?? 0) - (STATUS_ORDER[b.status] ?? 0));
  }, [vacancies]);

  return {
    vacancyGroupId: id,
    isLoading,
    hasActiveApplications,
    hasActivePublications,
    vacancies: sortedVacancies,
    rerenderCount,
    refreshData,
    setRerenderCount,
  };
}
